// Settings tab: the email editor (Profile/Email.vue; CLERK.md § Several emails per user). Shared
// with the dialog footer (OK, Cancel) and EmailChange. Changes take effect at once, except
// removals: marked first (undoable), deleted when the editor closes with OK or a click outside;
// Cancel or Escape forgets them. Already-made changes are never reversed.

import {ref} from "vue";
import {authFetch} from "@/composables/useAuthFetch";
import {ASSISTANT_ROW_ID, removeAssistant} from "@/composables/useAssistant";

const isEditingEmails = ref(false);
const pendingRemovalIds = ref([]);
const isBusy = ref(false);
const errorMessage = ref("");
const announcement = ref(""); // for screen readers (a live region in Email.vue)
// a promotion under way: shown as primary at once (EmailEditor), so the list moves on the click,
// not ~1.5 s later when the server is done; back to null when it is (or failed: it moves back)
const promotingId = ref(null);

function getStatusMessage(status) {
  if (status === 401) return "Please sign in again.";
  if (status >= 500) return "Can't save right now. Please try again shortly.";
  return "That didn't work. Please try again.";
}

// POST to the fullstack's api/v1/user/email/<action>; {ok, message, ...data}; a refusal may
// carry a reason (create: "taken", the address is another account's)
export async function postEmailAction(action, body) {
  const response = await authFetch(`/api/v1/user/email/${action}`, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (response.ok) return {ok: true, ...data};
  const message = data.statusMessage || getStatusMessage(response.status);
  return {ok: false, message, reason: data.data?.reason};
}

async function reloadUser(user) {
  try {
    await window.Clerk?.session?.touch();
    await user?.reload();
  } catch {
    // the next load shows it
  }
}

// one change at a time; the user is reloaded after it, whatever the outcome
async function runAction(user, action, body) {
  if (isBusy.value) return false;
  isBusy.value = true;
  errorMessage.value = "";
  const result = await postEmailAction(action, body);
  await reloadUser(user);
  isBusy.value = false;
  if (!result.ok) errorMessage.value = result.message;
  return result.ok;
}

function openEmails() {
  isEditingEmails.value = true;
}

function discardEmails() {
  if (isBusy.value) return;
  pendingRemovalIds.value = [];
  errorMessage.value = "";
  isEditingEmails.value = false;
}

function togglePendingRemoval(id) {
  const ids = pendingRemovalIds.value;
  const isPending = ids.includes(id);
  pendingRemovalIds.value = isPending ? ids.filter((item) => item !== id) : [...ids, id];
  const what = id === ASSISTANT_ROW_ID ? "Assistant" : "Email";
  announcement.value = isPending ? `${what} kept` : `${what} marked for removal`;
}

async function promoteEmail(user, emailAddressId) {
  if (isBusy.value) return false;
  promotingId.value = emailAddressId;
  announcement.value = "Primary email changed";
  const isDone = await runAction(user, "make-primary", {emailAddressId});
  promotingId.value = null;
  if (!isDone) announcement.value = "Primary email not changed";
  return isDone;
}

// the assistant marked for removal: the link removed (not an address of this account)
async function removeMarkedAssistant() {
  if (!pendingRemovalIds.value.includes(ASSISTANT_ROW_ID)) return true;
  const result = await removeAssistant();
  if (!result.ok) errorMessage.value = result.message;
  if (result.ok) announcement.value = "Assistant removed";
  return result.ok;
}

// OK, a click outside or Enter: deletes the marked addresses and the marked assistant, then
// closes (stays open on failure)
async function confirmEmails(user) {
  const isAddress = (id) => id !== ASSISTANT_ROW_ID;
  const emailAddressIds = pendingRemovalIds.value.filter(isAddress);
  if (emailAddressIds.length && !(await runAction(user, "remove", {emailAddressIds}))) return false;
  if (emailAddressIds.length) announcement.value = "Email removed";
  if (!(await removeMarkedAssistant())) return false;
  pendingRemovalIds.value = [];
  isEditingEmails.value = false;
  return true;
}

export function useEmailEdit() {
  return {
    isEditingEmails, pendingRemovalIds, isBusy, errorMessage, announcement, promotingId,
    openEmails, discardEmails, togglePendingRemoval, promoteEmail, confirmEmails,
  };
}
